import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  // Settings, // used by the hidden Preferences / Notifications tabs
  // Bell, // used by the hidden Preferences / Notifications tabs
  Shield,
  Palette,
  Save,
  Camera,
  Edit,
  Loader2
} from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { generateAvatarUrl } from '@/utils/avatarUtils'
import UserAvatar from '@/components/UserAvatar'

const Profile = () => {
  const { user, updateProfile, companyUrl } = useAuthStore()
  const { 
    userProfile, 
    states, 
    isLoadingProfile, 
    fetchUserProfile, 
    fetchStates,
    updateUserProfile 
  } = useUserStore()
  const { toast } = useToast()

  // Debug logging
  console.log('🔍 Profile component render:', {
    user,
    companyUrl,
    userProfile,
    statesCount: states?.length || 0,
    isLoadingProfile
  })
  
  const [activeTab, setActiveTab] = useState('profile')
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    firstname: '',
    middlename: '',
    lastname: '',
    email: '',
    mobilephone: '',
    homephone: '',
    address: '',
    aptsuite: '',
    city: '',
    state: '',
    country: '',
    postalcode: '',
    birthdate: '',
    gender: '',
    textmessages: 0
  })

  // Fetch user profile data and states when component mounts
  useEffect(() => {
    if (companyUrl) {
      // Only fetch if not already loading and data doesn't exist
      if (!userProfile && !isLoadingProfile) {
        console.log('🔄 Profile component: Fetching user profile...')
        fetchUserProfile(companyUrl)
      }
      if ((!states || states.length === 0)) {
        console.log('🔄 Profile component: Fetching states...')
        fetchStates(companyUrl)
      }
    }
  }, [companyUrl, userProfile, states, isLoadingProfile, fetchUserProfile, fetchStates])

  // Helper function to convert MM/DD/YYYY to YYYY-MM-DD
  const convertDateFormat = (dateString) => {
    if (!dateString) return ''
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.includes('-') && dateString.length === 10) {
        return dateString
      }
      // Convert MM/DD/YYYY to YYYY-MM-DD
      const [month, day, year] = dateString.split('/')
      if (month && day && year) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      return ''
    } catch (error) {
      console.warn('Date conversion error:', error)
      return ''
    }
  }

  // Update form data when userProfile is loaded
  useEffect(() => {
    if (userProfile) {
      console.log('📝 Updating form data with user profile:', userProfile)
      setFormData({
        firstname: userProfile['user.firstname'] || '',
        middlename: userProfile['user.middlename'] || '',
        lastname: userProfile['user.lastname'] || '',
        email: userProfile['user.email'] || '',
        mobilephone: userProfile.mobilephone || '',
        homephone: userProfile.homephone || '',
        address: userProfile.address || '',
        aptsuite: userProfile.aptsuite || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        country: userProfile.country || '',
        postalcode: userProfile.postalcode || '',
        birthdate: convertDateFormat(userProfile.birthdate),
        gender: userProfile.gender || '',
        textmessages: userProfile.textmessages || 0
      })
    }
  }, [userProfile])

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    // Hidden for now (see the commented content blocks below)
    // { id: 'preferences', name: 'Preferences', icon: Settings },
    // { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'privacy', name: 'Privacy', icon: Shield }
  ]

  const handleSave = () => {
    updateUserProfile(formData)
    setIsEditing(false)
    toast({
      title: "Profile updated!",
      description: "Your profile information has been saved successfully.",
      variant: "success",
    })
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }



  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-[1.625rem] font-semibold leading-tight tracking-[-0.025em] text-slate-900">Profile settings</h1>
          <p className="mt-1 text-sm text-slate-500">Manage your account and preferences</p>
        </div>
      </div>

      {/* Profile Header Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card>
          <CardContent className="p-6">
            {isLoadingProfile ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                <span className="ml-2 text-sm text-slate-500">Loading profile...</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative">
                  <UserAvatar
                    name={`${formData.firstname} ${formData.lastname}`}
                    email={formData.email}
                    src={userProfile?.avatar || generateAvatarUrl(formData.email, formData.gender)}
                    className="w-24 h-24"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label="Change photo"
                    className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-white p-0"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="text-center sm:text-left flex-1">
                  <h2 className="mb-1 text-2xl font-semibold tracking-[-0.02em] text-slate-900">
                    {`${formData.firstname} ${formData.middlename} ${formData.lastname}`.trim() || 'User'}
                  </h2>
                  <p className="mb-1 text-slate-600">{formData.email}</p>
                  <p className="text-sm text-slate-500">
                    Member since {formatDate(userProfile?.created_at || new Date())}
                  </p>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(!isEditing)}
                  disabled={isLoadingProfile}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {isEditing ? 'Cancel' : 'Edit Profile'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "fitness" : "outline"}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon className="h-4 w-4 mr-2" />
              {tab.name}
            </Button>
          )
        })}
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'profile' && (
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <Input
                      value={formData.firstname}
                      onChange={(e) => handleInputChange('firstname', e.target.value)}
                      disabled={!isEditing}
                      placeholder="First name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Middle Name
                    </label>
                    <Input
                      value={formData.middlename}
                      onChange={(e) => handleInputChange('middlename', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Middle name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <Input
                      value={formData.lastname}
                      onChange={(e) => handleInputChange('lastname', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <Input
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                    type="email"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Phone
                    </label>
                    <Input
                      value={formData.mobilephone}
                      onChange={(e) => handleInputChange('mobilephone', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Mobile phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Home Phone
                    </label>
                    <Input
                      value={formData.homephone}
                      onChange={(e) => handleInputChange('homephone', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Home phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <Input
                    value={formData.birthdate}
                    onChange={(e) => handleInputChange('birthdate', e.target.value)}
                    disabled={!isEditing}
                    type="date"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fitness-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">Select gender</option>
                    <option value="m">Male</option>
                    <option value="f">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader>
                <CardTitle>Address Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    disabled={!isEditing}
                    placeholder="Street address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apt/Suite
                  </label>
                  <Input
                    value={formData.aptsuite}
                    onChange={(e) => handleInputChange('aptsuite', e.target.value)}
                    disabled={!isEditing}
                    placeholder="Apartment or suite number"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      disabled={!isEditing}
                      placeholder="City"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <select
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fitness-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state.stateCode} value={state.stateCode}>
                          {state.stateName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Country"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <Input
                      value={formData.postalcode}
                      onChange={(e) => handleInputChange('postalcode', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Postal code"
                    />
                  </div>
                </div>

                {/* Hidden for now: Text Messages / Communication Preferences
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Communication Preferences</h4>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="textmessages"
                      checked={formData.textmessages === 1}
                      onChange={(e) => handleInputChange('textmessages', e.target.checked ? 1 : 0)}
                      disabled={!isEditing}
                      className="mr-2"
                    />
                    <label htmlFor="textmessages" className="text-sm text-gray-700">
                      Allow text messages
                    </label>
                  </div>
                </div>
                */}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Save Button for Profile Tab */}
        {activeTab === 'profile' && isEditing && (
          <div className="flex justify-end">
            <Button onClick={handleSave} className="bg-fitness-primary hover:bg-fitness-primary/90">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        )}

        {/* Hidden for now: Preferences and Notifications tabs
        {activeTab === 'preferences' && (
          <Card>
            <CardHeader>
              <CardTitle>App Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Units</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="radio" name="units" value="metric" className="mr-2" defaultChecked />
                    <span>Metric (kg, cm)</span>
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="units" value="imperial" className="mr-2" />
                    <span>Imperial (lbs, ft)</span>
                  </label>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Theme</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="radio" name="theme" value="light" className="mr-2" defaultChecked />
                    <span>Light</span>
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="theme" value="dark" className="mr-2" />
                    <span>Dark</span>
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="theme" value="auto" className="mr-2" />
                    <span>Auto (System)</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Workout Reminders</h4>
                    <p className="text-sm text-gray-600">Get notified about scheduled workouts</p>
                  </div>
                  <input type="checkbox" className="rounded" defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Progress Updates</h4>
                    <p className="text-sm text-gray-600">Weekly progress summaries</p>
                  </div>
                  <input type="checkbox" className="rounded" defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Achievement Alerts</h4>
                    <p className="text-sm text-gray-600">Celebrate your milestones</p>
                  </div>
                  <input type="checkbox" className="rounded" defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Email Notifications</h4>
                    <p className="text-sm text-gray-600">Receive updates via email</p>
                  </div>
                  <input type="checkbox" className="rounded" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        */}

        {activeTab === 'privacy' && (
          <Card>
            <CardHeader>
              <CardTitle>Privacy & Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {/* Hidden for now
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Profile Visibility</h4>
                    <p className="text-sm text-gray-600">Make your profile visible to other users</p>
                  </div>
                  <input type="checkbox" className="rounded" />
                </div>
                */}

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">Data Analytics</h4>
                    <p className="text-sm text-gray-600">Help improve the app with usage data</p>
                  </div>
                  <input type="checkbox" className="rounded" defaultChecked />
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4">Account Actions</h4>
                <div className="space-y-2">
                  <Button variant="outline" className="w-full sm:w-auto">
                    Change Password
                  </Button>
                  {/* Hidden for now
                  <Button variant="outline" className="w-full sm:w-auto">
                    Download My Data
                  </Button>
                  <Button variant="destructive" className="w-full sm:w-auto">
                    Delete Account
                  </Button>
                  */}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>

      {/* Save Button */}
      {isEditing && activeTab === 'profile' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end"
        >
          <Button variant="fitness" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </motion.div>
      )}
    </div>
  )
}

export default Profile